import React, { useState } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Paper,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import DescriptionIcon from '@material-ui/icons/Description';
import CloseIcon from '@material-ui/icons/Close';
import RefreshIcon from '@material-ui/icons/Refresh';
import firebase from "firebase/app";
import "firebase/storage";
import "firebase/database";
import VideoAnalysisService from '../../services/VideoAnalysisService';
import AIEvaluationService from '../../services/AIEvaluationService';
import swal from 'sweetalert';

const statusChip = (qd) => {
  // For video questions, use a different logic since they're always evaluated
  if (qd.evaluationType === 'video') {
    const percentage = qd.maxScore > 0 ? (qd.score / qd.maxScore) * 100 : 0;
    if (percentage >= 80) {
      return <Chip icon={<CheckCircleIcon style={{ color: '#fff' }} />} label="Excellent" style={{ background: '#43a047', color: '#fff', fontWeight: 700 }} size="small" />;
    } else if (percentage >= 60) {
      return <Chip icon={<CheckCircleIcon style={{ color: '#fff' }} />} label="Good" style={{ background: '#4caf50', color: '#fff', fontWeight: 700 }} size="small" />;
    } else if (percentage >= 40) {
      return <Chip icon={<RemoveCircleOutlineIcon style={{ color: '#fff' }} />} label="Fair" style={{ background: '#ffa726', color: '#fff', fontWeight: 700 }} size="small" />;
    } else {
      return <Chip icon={<CancelIcon style={{ color: '#fff' }} />} label="Needs Improvement" style={{ background: '#e53935', color: '#fff', fontWeight: 700 }} size="small" />;
    }
  }
  
  // For other question types, use the original logic
  if (qd.isCorrect) {
    return <Chip icon={<CheckCircleIcon style={{ color: '#fff' }} />} label="Correct" style={{ background: '#43a047', color: '#fff', fontWeight: 700 }} size="small" />;
  } else if (qd.score > 0) {
    return <Chip icon={<RemoveCircleOutlineIcon style={{ color: '#fff' }} />} label="Partial" style={{ background: '#ffa726', color: '#fff', fontWeight: 700 }} size="small" />;
  } else {
    return <Chip icon={<CancelIcon style={{ color: '#fff' }} />} label="Incorrect" style={{ background: '#e53935', color: '#fff', fontWeight: 700 }} size="small" />;
  }
};

const SubmissionDetailsAccordion = ({ examData, selectedSubmission, setSelectedSubmission, calculateSectionScore }) => {
  const [videoDialog, setVideoDialog] = useState({ open: false, videoUrl: '', videoPath: '', questionText: '' });
  const [pdfDialog, setPdfDialog] = useState({ open: false, pdfUrl: '', pdfPath: '', questionText: '' });
  const [reevaluatingQuestions, setReevaluatingQuestions] = useState({}); // Track which questions are being re-evaluated

  const handlePlayVideo = async (userAnswer, questionText) => {
    try {
      let videoUrl = userAnswer?.videoUrl;
      
      // If no direct URL, try to get it from videoPath
      if (!videoUrl && userAnswer?.videoPath) {
        const fullPath = `submissions/${userAnswer.videoPath}`;
        const storageRef = firebase.storage().ref(fullPath);
        videoUrl = await storageRef.getDownloadURL();
      }
      
      if (videoUrl) {
        setVideoDialog({
          open: true,
          videoUrl: videoUrl,
          videoPath: userAnswer?.videoPath || '',
          questionText: questionText
        });
      } else {
        alert('Video not found or unavailable');
      }
    } catch (error) {
      console.error('Error loading video:', error);
      alert('Error loading video. Please try again.');
    }
  };

  const handleCloseVideo = () => {
    setVideoDialog({ open: false, videoUrl: '', videoPath: '', questionText: '' });
  };

  const handleOpenPdf = async (userAnswer, questionText) => {
    try {
      let pdfUrl = userAnswer?.pdfFile?.url;
      
      // If no direct URL, try to get it from pdfPath (same as video recordings)
      if (!pdfUrl && userAnswer?.pdfFile?.pdfPath) {
        const fullPath = `submissions/${userAnswer.pdfFile.pdfPath}`;
        const storageRef = firebase.storage().ref(fullPath);
        pdfUrl = await storageRef.getDownloadURL();
      }
      
      if (pdfUrl) {
        setPdfDialog({
          open: true,
          pdfUrl: pdfUrl,
          pdfPath: userAnswer?.pdfFile?.pdfPath || '',
          questionText: questionText
        });
      } else {
        alert('PDF not found or unavailable');
      }
    } catch (error) {
      console.error('Error loading PDF:', error);
      alert('Error loading PDF. Please try again.');
    }
  };

  const handleClosePdf = () => {
    setPdfDialog({ open: false, pdfUrl: '', pdfPath: '', questionText: '' });
  };

  // Re-evaluate video question with Google Speech-to-Text
  const handleReevaluateVideoQuestion = async (questionId, answer, questionIndex) => {
    // Find the question object from examData
    let question = null;
    for (const section of examData.sections) {
      question = section.questions.find(q => q.id === questionId);
      if (question) break;
    }

    if (!question) {
      swal("Error", "Question not found. Cannot re-evaluate.", "error");
      return;
    }

    // Check if video path exists
    if (!answer?.videoPath && !answer?.videoUrl) {
      swal("Error", "Video not found. Cannot re-evaluate.", "error");
      return;
    }

    // Set loading state
    setReevaluatingQuestions(prev => ({ ...prev, [questionId]: true }));

    try {
      // Step 1: Get video path and convert to Firebase download URL
      let videoUrl = answer.videoUrl;
      
      if (!videoUrl && answer.videoPath) {
        // Convert videoPath to Firebase Storage download URL
        const fullPath = answer.videoPath.startsWith('submissions/') 
          ? answer.videoPath 
          : `submissions/${answer.videoPath}`;
        
        const storageRef = firebase.storage().ref(fullPath);
        videoUrl = await storageRef.getDownloadURL();
      }

      if (!videoUrl || !videoUrl.startsWith('http')) {
        throw new Error('Invalid video URL. Cannot proceed with transcription.');
      }

      // Step 2: Transcribe video using Google Speech-to-Text
      swal({
        title: "Transcribing Video",
        text: "Please wait while we transcribe the video...",
        icon: "info",
        button: false,
        closeOnClickOutside: false,
        closeOnEsc: false
      });

      const transcriptionResult = await VideoAnalysisService.transcribeVideo(videoUrl);
      
      if (!transcriptionResult.success || !transcriptionResult.transcript) {
        throw new Error(transcriptionResult.error || 'Transcription failed');
      }

      const transcript = transcriptionResult.transcript;

      // Step 3: Prepare answer object with transcript
      const enrichedAnswer = {
        ...answer,
        text: transcript, // Set transcript as answer text for evaluation
        transcript: transcript, // Also store in transcript field
        userEmail: selectedSubmission.email,
        examCode: selectedSubmission.examCode || examData.code
      };

      // Step 4: Evaluate using AI with evaluation points
      swal({
        title: "Evaluating Answer",
        text: "Please wait while we evaluate the answer...",
        icon: "info",
        button: false,
        closeOnClickOutside: false,
        closeOnEsc: false
      });

      const evaluationResult = await AIEvaluationService.evaluateQuestion(
        question,
        enrichedAnswer,
        null, // videoAnalysis (not needed for 'video' type)
        [] // images
      );

      // Step 5: Update only aiEvaluation in Firebase
      const examCode = selectedSubmission.examCode || examData.code;
      const userEmail = selectedSubmission.email;
      // Sanitize email: replace dots with commas for Firebase path compatibility
      const sanitizedEmail = userEmail.replace(/\./g, ',');
      const submissionRef = firebase.database().ref(`Exam_results/${examCode}/${sanitizedEmail}`);

      // Get current aiEvaluation
      const snapshot = await submissionRef.child('aiEvaluation').once('value');
      const currentAiEvaluation = snapshot.val() || { evaluations: {}, totalScore: 0, maxPossibleScore: 0 };

      // Update only the specific question's evaluation
      const updatedEvaluations = {
        ...currentAiEvaluation.evaluations,
        [questionId]: {
          ...evaluationResult,
          evaluationType: evaluationResult.evaluationType || 'text', // Keep original evaluation type
          questionId: questionId
        }
      };

      // Recalculate total score
      let newTotalScore = 0;
      let newMaxPossibleScore = 0;
      
      examData.sections.forEach(section => {
        section.questions.forEach(q => {
          const evaluation = updatedEvaluations[q.id];
          if (evaluation) {
            newTotalScore += evaluation.score || 0;
            newMaxPossibleScore += evaluation.maxScore || q.maxScore || q.marks || 0;
          }
        });
      });

      // Update aiEvaluation object
      const updatedAiEvaluation = {
        ...currentAiEvaluation,
        evaluations: updatedEvaluations,
        totalScore: newTotalScore,
        maxPossibleScore: newMaxPossibleScore,
        reevaluatedAt: firebase.database.ServerValue.TIMESTAMP,
        reevaluatedQuestions: {
          ...(currentAiEvaluation.reevaluatedQuestions || {}),
          [questionId]: firebase.database.ServerValue.TIMESTAMP
        }
      };

      // Step 6: Update answer object with transcript (same as submission method)
      const currentAnswers = (await submissionRef.child('answers').once('value')).val() || {};
      const updatedAnswer = {
        ...currentAnswers[questionId],
        transcript: transcript // Save transcript in answer object (same as PDF extractedText)
      };
      const updatedAnswers = {
        ...currentAnswers,
        [questionId]: updatedAnswer
      };

      // Save to Firebase - update both aiEvaluation and answers (keep everything else)
      await submissionRef.update({
        aiEvaluation: updatedAiEvaluation,
        answers: updatedAnswers
      });

      // Update local state with new evaluation data and transcript
      const updatedSubmission = {
        ...selectedSubmission,
        aiEvaluation: updatedAiEvaluation,
        answers: updatedAnswers
      };
      setSelectedSubmission(updatedSubmission);

      swal("Success", `Question ${questionIndex + 1} re-evaluated successfully!`, "success");

    } catch (error) {
      console.error('Re-evaluation error:', error);
      swal("Error", `Re-evaluation failed: ${error.message}`, "error");
    } finally {
      setReevaluatingQuestions(prev => {
        const newState = { ...prev };
        delete newState[questionId];
        return newState;
      });
    }
  };

  return (
  <Box mt={3}>
    <Typography variant="h6" gutterBottom style={{ fontWeight: 700, fontSize: 15 }}>Detailed Performance Analysis</Typography>
    <Typography variant="body2" color="textSecondary" gutterBottom style={{ fontWeight: 700, fontSize: 13 }}>
      Candidate: {selectedSubmission.name} - {selectedSubmission.email}
    </Typography>
    
    {/* Security Violations Summary */}
    <Paper elevation={2} style={{ marginBottom: 24, borderRadius: 12, padding: 16, background: '#fff3cd', border: '2px solid #ffc107' }}>
      <Typography variant="h6" style={{ fontWeight: 700, color: '#856404', marginBottom: 12 }}>
        Security Monitoring Summary
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '14px' }}>
            <div><strong>Total Violations:</strong> {selectedSubmission.totalCheatCount || 0}</div>
            <div><strong>Copy/Paste Attempts:</strong> {selectedSubmission.copyPaste || 0}</div>
            <div><strong>Tab Switches:</strong> {selectedSubmission.tab || 0}</div>
            <div><strong>Webcam Issues:</strong> {selectedSubmission.webcamOff || 0}</div>
          </div>
        </Grid>
        <Grid item xs={12} md={6}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '14px' }}>
            <div><strong>No Person Detected:</strong> {selectedSubmission.noPerson || 0}</div>
            <div><strong>Multiple Persons:</strong> {selectedSubmission.multiplePersons || 0}</div>
            <div><strong>Suspicious Objects:</strong> {selectedSubmission.suspiciousObjects || 0}</div>
            <div><strong>Face Detection Issues:</strong> {selectedSubmission.face || 0}</div>
          </div>
        </Grid>
      </Grid>
    </Paper>
    {examData.sections && (
      <>
        {examData.sections.map((section, sectionIndex) => {
          const sec = calculateSectionScore(section, selectedSubmission);
          return (
            <Paper key={sectionIndex} elevation={2} style={{ marginBottom: 24, borderRadius: 12, padding: 0 }}>
              <Accordion defaultExpanded style={{ boxShadow: 'none', background: 'transparent' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} style={{ background: '#f5faff', borderRadius: 12, minHeight: 28,height: 50, fontWeight: 700 }}>
                  <Box display="flex" justifyContent="space-between" width="100%" alignItems="center">
                    <Typography variant="h6" style={{ fontWeight: 700, color: '#1976d2', fontSize: 15 }}>{section.title}</Typography>
                    <Box textAlign="right">
                      <Typography variant="h6" color="primary" style={{ fontWeight: 700, fontSize: 14 }}>
                        Weighted: {(Number(sec.weightedScore) || 0).toFixed(2)} / {sec.weightage ?? 0}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" style={{ fontWeight: 700, fontSize: 13 }}>
                        Sec Score: {sec.rawScore ?? 0}/{sec.maxScore ?? 0}
                      </Typography>
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails style={{ background: '#fff', borderRadius: 0, padding: 0 }}>
                  <Grid container spacing={2} style={{ padding: 5, paddingLeft: 16, paddingRight: 16 }}>
                    <Grid item xs={12} >
                      <Typography variant="subtitle1" style={{ fontWeight: 700, fontSize: 12 }}>Section Calculation = ({sec.rawScore ?? 0}/{sec.maxScore ?? 0}) × {sec.weightage ?? 0} = {(Number(sec.weightedScore) || 0).toFixed(2)}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Divider style={{ margin: '12px 0' }} />
                      <Table size="small">
                        <TableHead>
                          <TableRow style={{ background: '#f5faff' }}>
                            <TableCell style={{ fontWeight: 700, fontSize: 14, padding: '6px 8px' }}>Q#</TableCell>
                            <TableCell style={{ fontWeight: 700, fontSize: 14, padding: '6px 8px' }}>Question</TableCell>
                            <TableCell style={{ fontWeight: 700, fontSize: 14, padding: '6px 8px' }}>User Answer</TableCell>
                            <TableCell style={{ fontWeight: 700, fontSize: 14, padding: '6px 8px' }}>Correct Answer</TableCell>
                            <TableCell style={{ fontWeight: 700, fontSize: 14, padding: '6px 8px' }}>Max Score</TableCell>
                            <TableCell style={{ fontWeight: 700, fontSize: 14, padding: '6px 8px' }}>User Score</TableCell>
                            <TableCell style={{ fontWeight: 700, fontSize: 14, padding: '6px 8px' }}>Status</TableCell>
                            <TableCell style={{ fontWeight: 700, fontSize: 14, padding: '6px 8px' }}>Feedback</TableCell>
                            <TableCell style={{ fontWeight: 700, fontSize: 14, padding: '6px 8px' }}>Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sec.questionDetails.map((qd, qidx) => {
                            // Get evaluation data from aiEvaluation.evaluations[questionId]
                            const evaluation = selectedSubmission.aiEvaluation?.evaluations?.[qd.questionId];
                            const confidence = evaluation?.confidence ?? qd.confidence;
                            const score = evaluation?.score ?? qd.score;
                            const textScore = evaluation?.textScore ?? qd.textScore;
                            
                            return (
                            <TableRow key={qidx} style={{ background: qidx % 2 === 0 ? '#f9f9fb' : '#fff' }}>
                              <TableCell style={{ fontWeight: 700, fontSize: 13, padding: '6px 8px' }}>{qidx + 1}</TableCell>
                              <TableCell style={{ fontWeight: 700, fontSize: 13, padding: '6px 8px' }}>{qd.questionText || qd.question || ''}</TableCell>
                              <TableCell style={{ fontSize: 13, padding: '6px 8px' }}>
                                <div style={{ maxWidth: 250, wordBreak: 'break-word', fontWeight: 700 }}>
                                  {qd.evaluationType === 'video' ? (
                                    <div>
                                      <div style={{ fontWeight: 600, color: '#9c27b0', marginBottom: 6, fontSize: '0.9em', background: '#f3e5f5', padding: '4px 8px', borderRadius: 4, border: '1px solid #ba68c8' }}>
                                        🎥 Video Analysis
                                      </div>
                                      <div style={{ fontSize: '0.85em', lineHeight: 1.4 }}>
                                        <div style={{ marginBottom: 4 }}>
                                          <strong>Video:</strong> {qd.userAnswer?.videoUrl || qd.userAnswer?.videoPath || 'N/A'}
                                        </div>
                                        <div style={{ marginBottom: 4 }}>
                                          <strong>Scenario:</strong> {qd.userAnswer?.selectedScenario || 'N/A'}
                                        </div>
                                        {qd.transcript && (
                                          <div style={{ marginTop: 8, padding: 8, background: '#f5f5f5', borderRadius: 4, fontSize: '0.8em' }}>
                                            <strong>Transcript:</strong> {qd.transcript.length > 100 ? qd.transcript.substring(0, 100) + '...' : qd.transcript}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ) : qd.evaluationType === 'scenario-text' ? (
                                    <div>
                                      <div style={{ fontWeight: 600, color: '#1976d2', marginBottom: 6, fontSize: '0.9em', background: '#e3f2fd', padding: '4px 8px', borderRadius: 4, border: '1px solid #90caf9' }}>
                                        📋 Scenario Response
                                      </div>
                                      <div style={{ fontSize: '0.85em', lineHeight: 1.5, maxHeight: '200px', overflow: 'hidden', position: 'relative' }}>
                                        {(() => {
                                          const rawAnswer = qd.userAnswer || qd.useranswer;
                                          
                                          // Handle different answer types
                                          let answer = '';
                                          if (typeof rawAnswer === 'string') {
                                            answer = rawAnswer;
                                          } else if (rawAnswer && typeof rawAnswer === 'object') {
                                            // For rich text answers with text property
                                            if (rawAnswer.text) {
                                              answer = typeof rawAnswer.text === 'string' ? rawAnswer.text : '';
                                            } else if (rawAnswer.videoUrl || rawAnswer.videoPath) {
                                              answer = 'Video response (see action column)';
                                            } else if (rawAnswer.pdfFile) {
                                              answer = 'PDF attachment (see action column)';
                                            } else {
                                              answer = JSON.stringify(rawAnswer);
                                            }
                                          }
                                          
                                          if (!answer || answer.trim() === '') {
                                            return <div style={{ color: '#999', fontStyle: 'italic' }}>No answer provided</div>;
                                          }
                                          
                                          const words = answer.split(' ');
                                          if (words.length > 200) {
                                            return (
                                              <div>
                                                <div style={{ marginBottom: 8 }}>
                                                  <div dangerouslySetInnerHTML={{ __html: words.slice(0, 200).join(' ') + '...' }} />
                                                </div>
                                                <div style={{ 
                                                  background: '#fff3cd', 
                                                  padding: '6px 8px', 
                                                  borderRadius: 4, 
                                                  border: '1px solid #ffc107',
                                                  fontSize: '0.75em',
                                                  color: '#856404',
                                                  fontWeight: 600
                                                }}>
                                                  📝 Response truncated at 200 words. Full response available in detailed view.
                                                </div>
                                              </div>
                                            );
                                          }
                                          return <div dangerouslySetInnerHTML={{ __html: answer }} />;
                                        })()}
                                      </div>
                                    </div>
                                  ) : (
                                    <div style={{ fontSize: '0.85em', lineHeight: 1.4 }}>
                                      {(() => {
                                        // Get answer from multiple sources: questionDetails, originalUserAnswer, or submission.answers
                                        const rawAnswer = qd.userAnswer || qd.useranswer || qd.originalUserAnswer;
                                        const submissionAnswer = selectedSubmission.answers?.[qd.questionId];
                                        
                                        // Use submission answer if available (most reliable source)
                                        const answerObj = submissionAnswer || rawAnswer;
                                        
                                        // Handle different answer types
                                        let answer = '';
                                        let transcript = null;
                                        let extractedText = null;
                                        
                                        if (typeof answerObj === 'string') {
                                          answer = answerObj;
                                        } else if (answerObj && typeof answerObj === 'object') {
                                          // For rich text answers with text property
                                          if (answerObj.text) {
                                            answer = typeof answerObj.text === 'string' ? answerObj.text : '';
                                          } else if (answerObj.videoUrl || answerObj.videoPath) {
                                            answer = 'Video response (see action column)';
                                          } else if (answerObj.pdfFile) {
                                            answer = 'PDF attachment (see action column)';
                                          } else if (Array.isArray(answerObj)) {
                                            answer = answerObj.join(', ');
                                          } else {
                                            answer = JSON.stringify(answerObj);
                                          }
                                          
                                          // Get transcript from answer object (check multiple locations)
                                          transcript = answerObj.transcript || null;
                                          
                                          // Get extracted text from PDF (check multiple locations)
                                          extractedText = answerObj.pdfFile?.extractedText || null;
                                        }
                                        
                                        // If still no transcript/extractedText, check rawAnswer as fallback
                                        if (!transcript && rawAnswer && typeof rawAnswer === 'object' && rawAnswer.transcript) {
                                          transcript = rawAnswer.transcript;
                                        }
                                        if (!extractedText && rawAnswer && typeof rawAnswer === 'object' && rawAnswer.pdfFile?.extractedText) {
                                          extractedText = rawAnswer.pdfFile.extractedText;
                                        }
                                        
                                        // Display transcript or extracted text if available
                                        const hasTranscript = transcript && transcript.trim();
                                        const hasExtractedText = extractedText && extractedText.trim();
                                        
                                        // If no answer text but we have transcript/extractedText, show that instead
                                        if ((!answer || answer.trim() === '') && !hasTranscript && !hasExtractedText) {
                                          return <div style={{ color: '#999', fontStyle: 'italic' }}>No answer provided</div>;
                                        }
                                        
                                        // If answer is empty but we have transcript/extractedText, set a default message
                                        if ((!answer || answer.trim() === '') && (hasTranscript || hasExtractedText)) {
                                          answer = hasTranscript ? 'Video response (see transcript below)' : 'PDF attachment (see extracted text below)';
                                        }
                                        
                                        const words = answer.split(' ');
                                        const displayAnswer = words.length > 150 ? (
                                          <div>
                                            <div style={{ marginBottom: 6 }}>
                                              <div dangerouslySetInnerHTML={{ __html: words.slice(0, 150).join(' ') + '...' }} />
                                            </div>
                                            <div style={{ 
                                              background: '#e8f5e8', 
                                              padding: '4px 6px', 
                                              borderRadius: 3, 
                                              border: '1px solid #4caf50',
                                              fontSize: '0.7em',
                                              color: '#2e7d32',
                                              fontWeight: 600
                                            }}>
                                              📝 Truncated at 150 words
                                            </div>
                                          </div>
                                        ) : <div dangerouslySetInnerHTML={{ __html: answer }} />;
                                        
                                        return (
                                          <div>
                                            {displayAnswer}
                                            
                                            {/* Display Video Transcript */}
                                            {hasTranscript && (
                                              <div style={{ color: '#000', fontSize: '0.8em', marginTop: 2, fontWeight: 700 }}>
                                                <div >
                                                  🎤 Video Transcript
                                                </div>
                                                <div >
                                                  {transcript}
                                                </div>
                                              </div>
                                            )}
                                            
                                            {/* Display PDF Extracted Text */}
                                            {hasExtractedText && (
                                              <div style={{ color: '#000', fontSize: '0.8em', marginTop: 2, fontWeight: 700 }}>
                                                <div >
                                                  📄 PDF Extracted Text
                                                </div>
                                                <div >
                                                  {extractedText}
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell style={{ fontSize: 13, padding: '6px 8px' }}>
                                <div style={{ maxWidth: 320, wordBreak: 'break-word', fontWeight: 700 }}>
                                  {qd.evaluationType === 'video' ? (
                                    <div>
                                      <div style={{ fontWeight: 600, color: '#9c27b0', marginBottom: 6, fontSize: '0.9em', background: '#f3e5f5', padding: '4px 8px', borderRadius: 4, border: '1px solid #ba68c8' }}>
                                        🎥 Video Analysis Scores
                                      </div>
                                      <div style={{ fontSize: '0.8em', lineHeight: 1.4 }}>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', marginBottom: 8 }}>
                                          <div style={{ padding: 4, background: '#e8f5e8', borderRadius: 3, border: '1px solid #4caf50' }}>
                                            <strong>Fluency:</strong> {qd.fluencyScore?.toFixed(2) || '0.00'}
                                          </div>
                                          <div style={{ padding: 4, background: '#e3f2fd', borderRadius: 3, border: '1px solid #2196f3' }}>
                                            <strong>Grammar:</strong> {qd.grammarScore?.toFixed(2) || '0.00'}
                                          </div>
                                          <div style={{ padding: 4, background: '#fff3e0', borderRadius: 3, border: '1px solid #ff9800' }}>
                                            <strong>Pronunciation:</strong> {qd.pronunciationScore?.toFixed(2) || '0.00'}
                                          </div>
                                          <div style={{ padding: 4, background: '#fce4ec', borderRadius: 3, border: '1px solid #e91e63' }}>
                                            <strong>MTI:</strong> {qd.mtiScore?.toFixed(2) || '0.00'}
                                          </div>
                                        </div>
                                        <div style={{ padding: 6, background: '#f5f5f5', borderRadius: 4, fontSize: '0.75em' }}>
                                          <strong>Processing Time:</strong> {qd.processingTime ? `${qd.processingTime}s` : 'N/A'}
                                        </div>
                                        {qd.contentMatchScore !== undefined && (
                                          <div style={{ 
                                            padding: 6, 
                                            background: qd.contentMatchScore >= 0.6 ? '#e8f5e8' : '#ffebee', 
                                            borderRadius: 4, 
                                            fontSize: '0.75em',
                                            marginTop: 4,
                                            border: `1px solid ${qd.contentMatchScore >= 0.6 ? '#4caf50' : '#f44336'}`
                                          }}>
                                            <strong>Content Match:</strong> {(qd.contentMatchScore * 100).toFixed(1)}%
                                            <span style={{ 
                                              color: qd.contentMatchScore >= 0.6 ? '#2e7d32' : '#d32f2f',
                                              marginLeft: 8,
                                              fontWeight: 600
                                            }}>
                                              {qd.contentMatchScore >= 0.6 ? '✅ Relevant' : '❌ Not Relevant'}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ) : qd.evaluationType === 'video-scenario' ? (
                                    <div>
                                      <div style={{ fontWeight: 600, color: '#9c27b0', marginBottom: 6, fontSize: '0.9em', background: '#f3e5f5', padding: '4px 8px', borderRadius: 4, border: '1px solid #ba68c8' }}>
                                        🎥 Video Scenario Evaluation
                                      </div>
                                      <div style={{ fontSize: '0.8em', lineHeight: 1.4 }}>
                                        {/* Scenario Score */}
                                        <div style={{ 
                                          padding: 8, 
                                          background: '#e8f5e8', 
                                          borderRadius: 6, 
                                          marginBottom: 8,
                                          border: '2px solid #4caf50',
                                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                        }}>
                                          <div style={{ fontWeight: 700, color: '#2e7d32', marginBottom: 4 }}>
                                            🎯 Scenario Score: {qd.scenarioScore?.toFixed(2) || '0.00'}/{qd.maxScore}
                                          </div>
                                          <div style={{ fontSize: '0.75em', color: '#666' }}>
                                            Selected Scenario: <strong>{qd.selectedScenario}</strong>
                                          </div>
                                        </div>
                                        
                                        {/* Video Analysis Scores */}
                                        <div style={{ marginBottom: 8 }}>
                                          <div style={{ fontWeight: 600, color: '#666', marginBottom: 4, fontSize: '0.75em' }}>
                                            📊 Video Analysis Scores:
                                          </div>
                                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
                                            <div style={{ padding: 4, background: '#e8f5e8', borderRadius: 3, border: '1px solid #4caf50' }}>
                                              <strong>Fluency:</strong> {qd.fluencyScore?.toFixed(2) || '0.00'}
                                            </div>
                                            <div style={{ padding: 4, background: '#e3f2fd', borderRadius: 3, border: '1px solid #2196f3' }}>
                                              <strong>Grammar:</strong> {qd.grammarScore?.toFixed(2) || '0.00'}
                                            </div>
                                            <div style={{ padding: 4, background: '#fff3e0', borderRadius: 3, border: '1px solid #ff9800' }}>
                                              <strong>Pronunciation:</strong> {qd.pronunciationScore?.toFixed(2) || '0.00'}
                                            </div>
                                            <div style={{ padding: 4, background: '#fce4ec', borderRadius: 3, border: '1px solid #e91e63' }}>
                                              <strong>MTI:</strong> {qd.mtiScore?.toFixed(2) || '0.00'}
                                            </div>
                                          </div>
                                        </div>
                                        
                                        {/* Matched Concepts */}
                                        {qd.matchedConcepts && qd.matchedConcepts.length > 0 ? (
                                          <div style={{ marginBottom: 8 }}>
                                            <div style={{ fontWeight: 600, color: '#666', marginBottom: 4, fontSize: '0.75em' }}>
                                              🎯 Matched Concepts:
                                            </div>
                                            <div style={{ 
                                              padding: 8, 
                                              background: '#e8f5e8', 
                                              borderRadius: 6,
                                              border: '1px solid #4caf50',
                                              fontSize: '0.75em'
                                            }}>
                                              <div style={{ 
                                                display: 'flex', 
                                                flexWrap: 'wrap', 
                                                gap: '4px',
                                                color: '#2e7d32',
                                                fontWeight: 600
                                              }}>
                                                {qd.matchedConcepts.map((concept, index) => (
                                                  <span key={index} style={{
                                                    background: '#c8e6c9',
                                                    padding: '2px 6px',
                                                    borderRadius: 12,
                                                    fontSize: '0.7em',
                                                    border: '1px solid #66bb6a'
                                                  }}>
                                                    {concept}
                                                  </span>
                                                ))}
                                              </div>
                                            </div>
                                          </div>
                                        ) : (
                                          <div style={{ marginBottom: 8 }}>
                                            <div style={{ fontWeight: 600, color: '#666', marginBottom: 4, fontSize: '0.75em' }}>
                                              ⚠️ No Relevant Concepts Found
                                            </div>
                                            <div style={{ 
                                              padding: 8, 
                                              background: '#ffebee', 
                                              borderRadius: 6,
                                              border: '1px solid #ef5350',
                                              fontSize: '0.75em',
                                              color: '#d32f2f'
                                            }}>
                                              The transcript shows minimal alignment with the selected scenario.
                                            </div>
                                          </div>
                                        )}
                                        
                                        {/* Processing Info */}
                                        <div style={{ padding: 6, background: '#f5f5f5', borderRadius: 4, fontSize: '0.75em' }}>
                                          <strong>Processing Time:</strong> {qd.processingTime ? `${qd.processingTime}s` : 'N/A'}
                                        </div>
                                      </div>
                                    </div>
                                  ) : qd.evaluationType === 'scenario-text' ? (
                                    <div>
                                      <div style={{ fontWeight: 600, color: '#43a047', marginBottom: 6, fontSize: '0.9em', background: '#e8f5e8', padding: '4px 8px', borderRadius: 4, border: '1px solid #4caf50' }}>
                                        📋 Evaluation Criteria Breakdown
                                      </div>
                                      {qd.criteriaBreakdown ? (
                                        <div style={{ fontSize: '0.8em', lineHeight: 1.4 }}>
                                          {Object.entries(qd.criteriaBreakdown).map(([criterion, details]) => (
                                            <div key={criterion} style={{ 
                                              marginBottom: 8, 
                                              padding: 10, 
                                              background: details.found ? '#f1f8e9' : '#ffebee', 
                                              borderRadius: 8,
                                              border: `2px solid ${details.found ? '#66bb6a' : '#ef5350'}`,
                                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                            }}>
                                              <div style={{ 
                                                fontWeight: 700, 
                                                color: details.found ? '#2e7d32' : '#d32f2f', 
                                                marginBottom: 4, 
                                                fontSize: '0.85em',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px'
                                              }}>
                                                <span style={{ 
                                                  fontSize: '1.2em',
                                                  color: details.found ? '#4caf50' : '#f44336'
                                                }}>
                                                  {details.found ? '✅' : '❌'}
                                                </span>
                                                {details.evaluationKey || details.criterion}
                                              </div>
                                              <div style={{ 
                                                fontWeight: 600, 
                                                color: '#666', 
                                                marginBottom: 3, 
                                                fontSize: '0.75em',
                                                background: details.found ? '#e8f5e8' : '#ffeaea',
                                                padding: '3px 6px',
                                                borderRadius: 3,
                                                display: 'inline-block'
                                              }}>
                                                📊 {details.score}/{details.maxScore} marks
                                              </div>
                                              <div style={{ 
                                                fontSize: '0.7em', 
                                                color: details.found ? '#2e7d32' : '#d32f2f', 
                                                fontWeight: 500, 
                                                lineHeight: 1.4,
                                                background: '#fff',
                                                padding: '6px 8px',
                                                borderRadius: 4,
                                                border: `1px solid ${details.found ? '#c8e6c9' : '#ffcdd2'}`
                                              }}>
                                                {details.feedback}
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      ) : (
                                        <div style={{ fontSize: '0.9em', lineHeight: 1.4, color: '#666', background: '#f5f5f5', padding: '8px', borderRadius: 4 }}>
                                          {qd.correctAnswer || qd.correctanswer || 'No evaluation criteria available'}
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    qd.correctAnswer || qd.correctanswer || ''
                                  )}
                                </div>
                              </TableCell>
                              <TableCell style={{ fontWeight: 700, fontSize: 13, padding: '6px 8px' }}>{qd.maxScore || qd.maxscore || ''}</TableCell>
                              <TableCell style={{ fontWeight: 700, fontSize: 13, padding: '6px 8px' }}>{qd.score || qd.marks || ''}</TableCell>
                              <TableCell style={{ fontSize: 13, padding: '6px 8px' }}>{statusChip(qd)}
                                {qd.confidence > 0 && (
                                  <div style={{ fontSize: '0.8em', color: '#666', marginTop: 2, fontWeight: 700 }}>
                                    {qd.confidence}% confidence
                                  </div>
                                )}
                              </TableCell>
                              <TableCell style={{ fontSize: 13, padding: '6px 8px' }}>
                                <div style={{ maxWidth: 250, wordBreak: 'break-word', fontSize: '0.9em', fontWeight: 700 }}>
                                  {qd.evaluationType === 'video' ? (
                                    <div>
                                      <div style={{ fontWeight: 600, color: '#9c27b0', marginBottom: 4 }}>
                                        🎥 Video Analysis Feedback
                                      </div>
                                      <div style={{ fontSize: '0.85em', lineHeight: 1.4 }}>
                                        {qd.feedback || 'No feedback available'}
                                      </div>
                                      <div style={{ marginTop: 6, fontSize: '0.8em' }}>
                                        <div style={{ fontWeight: 600, color: '#1976d2', marginBottom: 4 }}>
                                          📊 Raw Scores (out of 10):
                                        </div>
                                        <div style={{ fontSize: '0.75em', lineHeight: 1.3, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
                                          <div>Fluency: {qd.rawFluencyScore?.toFixed(2) || '0.00'}</div>
                                          <div>Grammar: {qd.rawGrammarScore?.toFixed(2) || '0.00'}</div>
                                          <div>Pronunciation: {qd.rawPronunciationScore?.toFixed(2) || '0.00'}</div>
                                          <div>MTI: {qd.rawMtiScore?.toFixed(2) || '0.00'}</div>
                                        </div>
                                      </div>
                                    </div>
                                  ) : qd.evaluationType === 'video-scenario' ? (
                                    <div>
                                      <div style={{ fontWeight: 600, color: '#9c27b0', marginBottom: 4 }}>
                                        🎥 Video Scenario Feedback
                                      </div>
                                      <div style={{ fontSize: '0.85em', lineHeight: 1.4 }}>
                                        {qd.feedback || 'No feedback available'}
                                      </div>
                                      {qd.matchedConcepts && qd.matchedConcepts.length > 0 && (
                                        <div style={{ marginTop: 6, fontSize: '0.8em' }}>
                                          <div style={{ fontWeight: 600, color: '#1976d2', marginBottom: 4 }}>
                                            📈 Scenario Summary:
                                          </div>
                                          <div style={{ fontSize: '0.75em', lineHeight: 1.3 }}>
                                            Found {qd.matchedConcepts.length} relevant concepts matching the scenario
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  ) : qd.evaluationType === 'scenario-text' ? (
                                    <div>
                                      <div style={{ fontWeight: 600, color: '#ff9800', marginBottom: 4 }}>
                                        📊 Overall Performance
                                      </div>
                                      <div style={{ fontSize: '0.85em', lineHeight: 1.4 }}>
                                        {qd.feedback || 'No feedback available'}
                                      </div>
                                      {qd.criteriaBreakdown && (
                                        <div style={{ marginTop: 6, fontSize: '0.8em' }}>
                                          <div style={{ fontWeight: 600, color: '#1976d2', marginBottom: 4 }}>
                                            📈 Summary:
                                          </div>
                                          <div style={{ fontSize: '0.75em', lineHeight: 1.3 }}>
                                            {Object.entries(qd.criteriaBreakdown).filter(([criterion, details]) => details.found).length} of {Object.keys(qd.criteriaBreakdown).length} criteria addressed
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <>
                                      {qd.feedback || 'No feedback available'}
                                      {qd.evaluationType && qd.evaluationType !== 'fallback' && (
                                        <div style={{ color: '#1976d2', fontSize: '0.8em', marginTop: 2, fontWeight: 700 }}>
                                          AI Evaluated ({qd.evaluationType})
                                        </div>
                                      )}
                                    </>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell style={{ fontSize: 13, padding: '6px 8px', textAlign: 'center' }}>
                                {(() => {
                                  // Debug logging for video questions only
                                  if (qd.evaluationType === 'video' || qd.type === 'video') {
                                    console.log('Video question debug for Q#', qidx + 1, ':', {
                                      evaluationType: qd.evaluationType,
                                      type: qd.type,
                                      originalUserAnswer: qd.originalUserAnswer,
                                      hasVideoUrl: !!qd.originalUserAnswer?.videoUrl,
                                      hasVideoPath: !!qd.originalUserAnswer?.videoPath,
                                      videoUrl: qd.originalUserAnswer?.videoUrl,
                                      videoPath: qd.originalUserAnswer?.videoPath
                                    });
                                  }
                                  return null;
                                })()}
                                {(qd.evaluationType === 'video' || qd.type === 'video') && (qd.originalUserAnswer?.videoUrl || qd.originalUserAnswer?.videoPath) ? (
                                  <Box display="flex" gap={1} alignItems="center">
                                    <Button
                                      variant="contained"
                                      color="primary"
                                      size="small"
                                      startIcon={<PlayArrowIcon />}
                                      onClick={() => handlePlayVideo(qd.originalUserAnswer, qd.questionText || qd.question)}
                                      style={{
                                        fontSize: '11px',
                                        padding: '4px 8px',
                                        minWidth: 'auto',
                                        background: '#9c27b0',
                                        color: '#fff',
                                        fontWeight: 600
                                      }}
                                    >
                                      Play Video
                                    </Button>
                                    {/* Only show Re-evaluate button if confidence is 0 or score is 0 or textScore is 0 */}
                                    {(confidence === 0 || score === 0 || textScore === 0) && (
                                      <Button
                                        variant="outlined"
                                        color="secondary"
                                        size="small"
                                        startIcon={reevaluatingQuestions[qd.questionId] ? <CircularProgress size={14} /> : <RefreshIcon />}
                                        onClick={() => handleReevaluateVideoQuestion(qd.questionId, qd.originalUserAnswer, qidx)}
                                        disabled={reevaluatingQuestions[qd.questionId]}
                                        style={{
                                          fontSize: '11px',
                                          padding: '4px 8px',
                                          minWidth: 'auto',
                                          borderColor: '#ff9800',
                                          color: '#ff9800',
                                          fontWeight: 600
                                        }}
                                      >
                                        {reevaluatingQuestions[qd.questionId] ? 'Evaluating...' : 'Re-evaluate'}
                                      </Button>
                                    )}
                                  </Box>
                                ) : (qd.evaluationType === 'video' || qd.type === 'video') ? (
                                  <span style={{ color: '#f44336', fontSize: '11px', fontWeight: 600 }}>No Video</span>
                                ) : (qd.originalUserAnswer?.pdfFile?.pdfPath || qd.originalUserAnswer?.pdfFile?.fileName) ? (
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    startIcon={<DescriptionIcon />}
                                    onClick={() => handleOpenPdf(qd.originalUserAnswer, qd.questionText || qd.question)}
                                    style={{
                                      fontSize: '11px',
                                      padding: '4px 8px',
                                      minWidth: 'auto',
                                      background: '#ff9800',
                                      color: '#fff',
                                      fontWeight: 600
                                    }}
                                  >
                                    View PDF
                                  </Button>
                                ) : (
                                  <span style={{ color: '#999', fontSize: '11px' }}>-</span>
                                )}
                              </TableCell>
                            </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Paper>
          );
        })}
      </>
    )}

    {/* Video Player Dialog */}
    <Dialog
      open={videoDialog.open}
      onClose={handleCloseVideo}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          backgroundColor: '#f5f5f5',
          borderRadius: 12
        }
      }}
    >
      <DialogTitle style={{ 
        background: 'linear-gradient(135deg, #9c27b0 0%, #673ab7 100%)',
        color: '#fff',
        fontWeight: 700,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <span>🎥 Video Response</span>
        <IconButton
          onClick={handleCloseVideo}
          style={{ color: '#fff' }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent style={{ padding: 20 }}>
        <Box mb={2}>
          <Typography variant="subtitle1" style={{ fontWeight: 600, color: '#333', marginBottom: 8 }}>
            Question:
          </Typography>
          <Typography variant="body2" style={{ 
            background: '#fff', 
            padding: 12, 
            borderRadius: 8, 
            border: '1px solid #e0e0e0',
            lineHeight: 1.5
          }}>
            {videoDialog.questionText}
          </Typography>
        </Box>
        
        <Box>
          <Typography variant="subtitle1" style={{ fontWeight: 600, color: '#333', marginBottom: 8 }}>
            Video Response:
          </Typography>
          <Box style={{
            background: '#000',
            borderRadius: 8,
            overflow: 'hidden',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
          }}>
            <video
              controls
              style={{
                width: '100%',
                height: 'auto',
                maxHeight: '60vh',
                display: 'block'
              }}
              preload="metadata"
            >
              <source src={videoDialog.videoUrl} type="video/webm" />
              <source src={videoDialog.videoUrl} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </Box>
        </Box>
      </DialogContent>
      
      <DialogActions style={{ padding: '16px 20px', background: '#f9f9f9' }}>
        <Button
          onClick={handleCloseVideo}
          variant="outlined"
          style={{
            borderColor: '#9c27b0',
            color: '#9c27b0',
            fontWeight: 600
          }}
        >
          Close
        </Button>
        <Button
          onClick={() => window.open(videoDialog.videoUrl, '_blank')}
          variant="contained"
          style={{
            background: '#9c27b0',
            color: '#fff',
            fontWeight: 600
          }}
        >
          Open in New Tab
        </Button>
      </DialogActions>
    </Dialog>

    {/* PDF Viewer Dialog */}
    <Dialog
      open={pdfDialog.open}
      onClose={handleClosePdf}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: 12,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle style={{ 
        background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
        color: '#fff',
        fontWeight: 700,
        fontSize: 18,
        padding: '16px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box style={{ display: 'flex', alignItems: 'center' }}>
          <DescriptionIcon style={{ marginRight: 8, fontSize: 24 }} />
          PDF Document
        </Box>
        <IconButton
          onClick={handleClosePdf}
          style={{ color: '#fff', padding: 4 }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent style={{ padding: '20px', background: '#fafafa' }}>
        <Box>
          <Typography variant="subtitle1" style={{ fontWeight: 600, color: '#333', marginBottom: 8 }}>
            Question: {pdfDialog.questionText}
          </Typography>
          <Typography variant="body2" style={{ color: '#666', marginBottom: 16 }}>
            PDF Response Document
          </Typography>
        </Box>
        
        <Box style={{
          background: '#fff',
          borderRadius: 8,
          overflow: 'hidden',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          border: '1px solid #e0e0e0'
        }}>
          <iframe
            src={pdfDialog.pdfUrl}
            style={{
              width: '100%',
              height: '60vh',
              border: 'none',
              display: 'block'
            }}
            title="PDF Document"
          />
        </Box>
      </DialogContent>
      
      <DialogActions style={{ padding: '16px 20px', background: '#f9f9f9' }}>
        <Button
          onClick={handleClosePdf}
          variant="outlined"
          style={{
            borderColor: '#ff9800',
            color: '#ff9800',
            fontWeight: 600
          }}
        >
          Close
        </Button>
        <Button
          onClick={() => window.open(pdfDialog.pdfUrl, '_blank')}
          variant="contained"
          style={{
            background: '#ff9800',
            color: '#fff',
            fontWeight: 600
          }}
        >
          Open in New Tab
        </Button>
      </DialogActions>
    </Dialog>
  </Box>
  );
};

export default SubmissionDetailsAccordion; 